import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Col, Row } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import ModalBody from 'react-bootstrap/ModalBody';
import ModalFooter from 'react-bootstrap/ModalFooter';
import ColumnsChart from '../../../components/ApexCharts/ColumnsChart';
import DonutCustomLegend from '../../../components/ApexCharts/CustomLegends/DonutCustomLegend';
import DonutChart from '../../../components/ApexCharts/DonutChart';
import Blockquote from '../../../components/Blockquote';
import Button from '../../../components/Button';
import CalloutCard from '../../../components/CalloutCard';
import ModalHeader from '../../../components/ModalHeader/ModalHeader';
import {
  addOrRemoveDaysToGivenDate,
  formatDate,
  formatPrice,
} from '../../../utils/utils';
// import  default as CustomLink  from '../../../components/Link';
import { Link } from 'react-router-dom';

const accumulatedWealthData = {
  // The "grid padding - left" setting is a hack to reduce the huge margin to the left that the donut renders.
  // See: https://github.com/apexcharts/apexcharts.js/issues/1272
  grid: {
    padding: {
      left: -20,
    },
  },
  legend: {
    show: false,
  },

  dataLabels: {
    enabled: false,
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        grid: {
          padding: {
            left: 0,
          },
        },
        legend: {
          show: false,
        },
        title: {},
      },
    },
  ],
  colors: ['#074CBC', '#07B4A0', '#92E3A9'],
  series: [423692, 246244, 31212],
  labels: ['Compensation', 'Retirement', 'Other Benefits'],
  tooltip: {
    enabled: false,
  },

  // DISPLAY TEXT IN DONUT CENTRE:
  plotOptions: {
    pie: {
      donut: {
        labels: {
          show: true,
          name: {
            offsetY: 10,
          },
          total: {
            show: true,
            showAlways: true,
            fontSize: '18px',
            fontFamily: 'Roboto, Arial, sans-serif',
            fontWeight: 500,
            label: `$671,148`,
            formatter: function (a: any) {
              return '';
            },
          },
        },
      },
    },
  },
};

const columnChartData = {
  chart: {
    stacked: true,
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  colors: ['#07B4A0', '#074CBC'],
  series: [
    {
      name: 'series1',
      data: [250000, 900000],
    },
    {
      name: 'series2',
      data: [480000, 1700000],
    },
  ],

  dataLabels: {
    enabled: false,
  },
  fill: {
    opacity: 1,
  },
  legend: {
    show: false,
  },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: '40%',
      distributed: false,
    },
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        legend: {
          position: 'bottom',
          offsetX: -10,
          offsetY: 0,
        },
      },
    },
  ],
  tooltip: {
    enabled: false,
  },
  xaxis: {
    categories: [63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73],
    overwriteCategories: ['Age 65', 'Age 70'],
    tickAmount: 8,
  },
  yaxis: {
    title: {
      text: '',
    },
    min: 0,
    max: 1500000,
    tickAmount: 6,

    labels: {
      formatter: function (a: any) {
        return formatPrice(a, 'en-US', {
          currency: 'USD',
          style: 'currency',
          minimumFractionDigits: 0,
        });
      },
      style: {
        fontSize: '12px',
      },
    },
  },
};

const downloadBroshureButton = [
  <a
    className={'text-decoration-none'}
    href={`${
      process.env.PUBLIC_URL
    }/${'total-rewards-statement-demo-example_v1.pdf'}`}
    target={'_blank'}
    rel="noreferrer"
  >
    <Button
      variant="secondary-outline"
      rightIcon={light('file-pdf')}
      className="callout-button"
      size="sm"
    >
      Download Brochure
    </Button>
  </a>,
];

const retirementToolButton = [
  <Link to={'/retirement-planning'} className={'text-decoration-none'}>
    <Button
      variant="secondary-outline"
      rightIcon={light('person-cane')}
      className="callout-button"
      size="sm"
    >
      Retirement Planning
    </Button>
  </Link>,
];

export type AccumulatedWealthModalProps = React.PropsWithoutRef<{
  showAccumulatedWealthModal: boolean;
  handleCloseAccumulatedWealthModal: () => void;
}>;

const AccumulatedWealthModal = ({
  showAccumulatedWealthModal,
  handleCloseAccumulatedWealthModal,
}: AccumulatedWealthModalProps) => {
  return (
    <Modal
      show={showAccumulatedWealthModal}
      onHide={handleCloseAccumulatedWealthModal}
      size={'xl'}
    >
      <ModalHeader closeButton={true}>
        <Col>
          <Row>
            {' '}
            <h5>Accumulated Wealth</h5>
          </Row>
          <Row>
            <span className="text-light-grey text-fs-6 fw-light">
              {`(As of ${formatDate(
                addOrRemoveDaysToGivenDate(new Date(), -1)
              )})`}
            </span>
          </Row>
        </Col>
      </ModalHeader>
      <ModalBody>
        <Row className="pt-12 px-lg-0 px-7">
          <Col className="px-0 col-12 col-lg-5">
            <DonutChart
              series={accumulatedWealthData.series}
              options={accumulatedWealthData}
            />
          </Col>
          <Col className="pt-3 lh-xs pe-10">
            <DonutCustomLegend
              options={accumulatedWealthData}
              starredCategory="Other Benefits"
            />
            <Row className="text-fs-6 fw-light lh-xs text-ligther-grey pt-7">
              <Col>
                * Your <span className="fw-bolder">pension amount </span>is an
                estimate of the amount payable as of your Normal Retirement Age.
                Your accrued monthly amount is $1,212. If you retire earlier
                than the stated retirement age this amount will be less.
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="pt-10 lh-xs px-4">
          <Blockquote>
            <section>
              <Row>
                <Col className="title-span fw-bolder">
                  Let's help you understand this information
                </Col>
              </Row>
              <Row>
                <Col>
                  {' '}
                  We have added up the pieces of your wealth and projected them
                  to various retirement ages. Use the suggestions below to help
                  you secure your future. For more advanced modeling using the
                  Retirement Planning tool will help you know better how
                  prepared you are, and how to become better prepared.
                </Col>
              </Row>
            </section>
          </Blockquote>
        </Row>
        <Row className="pt-13">
          <Col className="col-lg-9">
            <Row>
              {' '}
              <h5>Estimated Future Wealth</h5>
            </Row>
          </Col>
          <Col className="text-end">
            <a
              className={'text-decoration-none'}
              href={`${process.env.PUBLIC_URL}/${'dc-strategy-demo.pdf'}`}
              target={'_blank'}
              rel="noreferrer"
            >
              <Button variant="primary" size="sm">
                Explore Your Future
              </Button>
            </a>
          </Col>
        </Row>
        <Row className="pt-13">
          <Col>
            <ColumnsChart
              series={columnChartData.series}
              options={columnChartData}
            />
          </Col>
        </Row>
        <Row className="pt-11">
          <Col>
            <Row>
              <h5>Suggestions</h5>
            </Row>
          </Col>
        </Row>
        <Row className="pt-10 lh-xs px-4">
          <Blockquote>
            <section>
              <Row>
                <Col>
                  {' '}
                  <span className="title-span fw-bolder">
                    Income Replacement:
                  </span>{' '}
                  Retirement is not far off but you are currently projected to
                  be able to replace only 69% of your income at age 65. Go to
                  Retirement Planning and try the following: 1) make sure all
                  your retirement assets are included; 2) check the income
                  replacement assumption - can you lower your goal?; 3) consider
                  retiring a few years later; 4) increase your contributions if
                  you think you can afford it.
                </Col>
              </Row>
            </section>
          </Blockquote>
        </Row>
        <Row className="mt-10">
          <Col>
            <CalloutCard
              cardIcon={light('screwdriver-wrench')}
              buttons={downloadBroshureButton}
            >
              <Row className="content-row">
                <Col>
                  <span className="bolder-text me-1">
                    Retirement Preparation:
                  </span>
                  Are you thinking about retirement? Here is a brochure that
                  does some of that thinking for you.
                </Col>
              </Row>
            </CalloutCard>
          </Col>
        </Row>
        <Row className="mt-10">
          <Col>
            <CalloutCard
              cardIcon={light('screwdriver-wrench')}
              buttons={retirementToolButton}
            >
              <Row className="content-row">
                <Col>
                  <span className="bolder-text me-1">Retirement Assets:</span>
                  Are there other retirement assets that you have not yet
                  included in your modeling? Enter these in the Retirement
                  Planning tool under advanced assumptions to more accurately
                  reflect your current situation.
                </Col>
              </Row>
            </CalloutCard>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary-outline"
          onMouseDown={handleCloseAccumulatedWealthModal}
        >
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
export default AccumulatedWealthModal;
